import React, { useState, useCallback, useRef } from 'react';
import { Input, AutoComplete, Tooltip, message } from 'antd';
import { fieldAutoSuggest, getEmployeeDetails } from '../../../services/selfOnboarding';
import debounce from 'lodash.debounce';
import './TimeAndPayTable.css';

const copyNotAllowedFields = ['inTime', 'outTime'];
const requiredFieldsTime = ['employeeId', 'name', 'inDate', 'inTime'];
const requiredFieldsPay = ['employeeId', 'name', 'inDate', 'payCode', 'amount'];

const getStatusColor = (status) => {
  if (status === 'success') return '#4caf50'; // green
  if (status === 'processing') return '#ffc107'; // yellow
  if (status === 'failed') return '#f44336'; // red
  return 'transparent';
};

const StatusIndicator = ({ status, error }) => {
  if (!status) return null;
  const color = getStatusColor(status);
  const circle = (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: color,
        marginRight: 6,
        verticalAlign: 'middle',
        border: '1px solid #ccc',
      }}
    />
  );
  if (status === 'failed' && error) {
    return <Tooltip title={error} key={error}>{circle}</Tooltip>;
  }
  return circle;
};

function getNextNavigableColIdx(columnKeys, currentIdx, direction) {
  // Define columns to skip for navigation
  const skipFields = ['name'];
  let idx = currentIdx;
  while (true) {
    idx = direction === 'right' ? idx + 1 : idx - 1;
    if (idx < 0 || idx >= columnKeys.length) return currentIdx; // stay if out of bounds
    if (!skipFields.includes(columnKeys[idx])) return idx;
  }
}

/** Handles keyboard navigation + copy-above */
const handleInputKeyDown = (e, rowIdx, colIdx, field, rows, columnKeys, setRows, focusCell, setJustCopied, setIsDirty) => {
  // Only handle arrow keys for cell navigation if dropdown is NOT open
  if (e.shiftKey) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextCol = getNextNavigableColIdx(columnKeys, colIdx, 'right');
      if (nextCol !== colIdx) focusCell(rowIdx, nextCol);
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevCol = getNextNavigableColIdx(columnKeys, colIdx, 'left');
      if (prevCol !== colIdx) focusCell(rowIdx, prevCol);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) focusCell(rowIdx + 1, colIdx);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIdx > 0) focusCell(rowIdx - 1, colIdx);
      return;
    }
  }

  if (copyNotAllowedFields.includes(field)) return;

  // Only trigger copy if value is exactly "9" when Tab is pressed
  if (e.key === 'Tab' && e.target.value === '9') {
    e.preventDefault();
    if (rowIdx > 0) {
      setRows((prev) =>
        prev.map((r, idx) => {
          if (idx === rowIdx) {
            // EmployeeId special logic
            if (field === 'employeeId') {
              const above = rows[rowIdx - 1];
              return {
                ...r,
                employeeId: above.employeeId,
                name: above.name,
                payscale: above.payscale,
                group: above.group,
                level: above.level,
                pa: above.pa,
                costCenter: above.costCenter,
                nameLocked: !!above.name,
                task: above.task, 
                sub_task: above.sub_task,
              };
            }
            // wbs_io special logic
            if (field === 'wbs_io') {
              return { ...r, [field]: rows[rowIdx - 1][field], wbsIoInvalid: false };
            }
            // payCode special logic - copy unit and format amount with $ if unit is MONEY
            if (field === 'payCode') {
              const above = rows[rowIdx - 1];
              const currentAmount = r.amount?.toString() || '';
              let formattedAmount = currentAmount;
              
              if (above.unit === 'MONEY') {
                // Add $ prefix if not already present
                if (currentAmount && !currentAmount.startsWith('$')) {
                  formattedAmount = `$${currentAmount}`;
                }
              } else {
                // Remove $ prefix if present
                formattedAmount = currentAmount.replace(/^\$/, '');
              }
              
              return {
                ...r,
                payCode: above.payCode,
                unit: above.unit,
                amount: formattedAmount,
              };
            }
            // Default copy
            return { ...r, [field]: rows[rowIdx - 1][field] };
          }
          return r;
        }),
      );
      setIsDirty(true);
      setJustCopied(true);
      // Move focus to next cell
      if (colIdx < columnKeys.length - 1) focusCell(rowIdx, colIdx + 1);
    }
    return;
  }
  // Otherwise, allow normal typing and tabbing (including 9 anywhere in the value)
};

export const getNextDate = (mmddyy) => {
  if (!mmddyy || mmddyy.length !== 8) return mmddyy;
  const [mm, dd, yy] = mmddyy.split('-');
  // Convert to Date object (assume 20xx for 2-digit year)
  const yyyy = parseInt(yy, 10) < 50 ? '20' + yy : '19' + yy;
  const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  date.setDate(date.getDate() + 1);
  const nextMM = String(date.getMonth() + 1).padStart(2, '0');
  const nextDD = String(date.getDate()).padStart(2, '0');
  const nextYY = String(date.getFullYear()).slice(-2);
  return `${nextMM}-${nextDD}-${nextYY}`;
};

const updateCell = (key, field, value, setRows, setIsDirty) => {
  setRows((prev) =>
    prev.map((r) => {
      if (r.key === key) {
        if (r[field] !== value) setIsDirty(true);

        // Bidirectional clearing for task/sub_task
        if ((field === 'task' || field === 'sub_task') && value === '') {
          return { ...r, task: '', sub_task: '' };
        }
        // Bidirectional clearing for payscale/group
        if ((field === 'payscale' || field === 'group') && value === '') {
          return { ...r, payscale: '', group: '' };
        }
        // Bidirectional clearing for pa/costCenter
        if ((field === 'pa' || field === 'costCenter') && value === '') {
          return { ...r, pa: '', costCenter: '' };
        }

        // --- Autofill outDate logic ---
        let updatedRow = { ...r, [field]: value };

        // Only autofill if inDate, inTime, and outTime are present and valid
        const { inDate, inTime, outTime } = {
          ...updatedRow,
          [field]: value,
        };

        // Helper to parse time string "HH:MM" to minutes
        const parseTime = (t) => {
          if (!t || t.length !== 5) return null;
          const [h, m] = t.split(':');
          return parseInt(h, 10) * 60 + parseInt(m, 10);
        };

        if (inDate && inTime && outTime && isValidShortDate(inDate) && isValidTime(inTime) && isValidTime(outTime)) {
          const inMins = parseTime(inTime);
          const outMins = parseTime(outTime);
          // If outTime is less than or equal to inTime, assume next day
          let newOutDate = inDate;
          if (outMins <= inMins) {
            newOutDate = getNextDate(inDate);
          }
          updatedRow.outDate = newOutDate;
        }

        return updatedRow;
      }
      return r;
    }),
  );
};

/** Reusable CellRenderer component */
const CellRenderer = ({ record, rowIdx, colIdx, field, type, rows, columnKeys, inputRefs, setRows, setIsDirty, setFocusedCell, focusCell, selectedBranch }) => {
  const [options, setOptions] = useState([]);
  const justCopiedRef = useRef(false);
  const [searchValue, setSearchValue] = useState(''); // Track input value for dropdown logic
  const isRowSuccess = record.transactionStatus === 'success';

  // Debounced fetcher
  const debouncedFetch = useCallback(
    debounce(async (searchText, record) => {
      if (searchText.length < 2) {
        setOptions([]);
        return;
      }
      // Prepare extra parameter for specific fields
      let extraParam = {};
      if (field === 'sub_task') {
        if (!record.task) {
          setOptions([]);
          message.error('Please select a Task before searching for Sub Task.');
          return;
        }
        extraParam.selectedValue = record.task || '';
      }
      if (field === 'group') {
        if (!record.payscale) {
          setOptions([]);
          message.error('Please select a Payscale before searching for Group.');
          return;
        }
        extraParam.selectedValue = record.payscale || '';
      }
      if (field === 'costCenter') {
        if (!record.pa) {
          setOptions([]);
          message.error('Please select a PA before searching for Cost Center.');
          return;
        }
        extraParam.selectedValue = record.pa || '';
      }
      try {
        let filters = {};
        filters.branch = selectedBranch;
        const { data } = await fieldAutoSuggest({ parameter: field, searchText, ...extraParam, filters });
        // For all autoSuggest fields, expect array of { name, description }
        if (field === 'employeeId' && data && Array.isArray(data[field])) {
          setOptions(data[field].map((item) => ({ value: item })));
        } else if (field === 'payCode' && data && Array.isArray(data[field])) {
          setOptions(
            data[field].map((item) => ({
              value: item.name,
              label: item.name, // Just use plain string
              description: item.description, // Store for custom rendering
              unit: item.unit, // Pass unit for use in onSelect
            })),
          );
        } else if (field !== 'employeeId' && field !== 'payCode' && data && Array.isArray(data[field])) {
          setOptions(
            data[field].map((item) => ({
              value: item.name,
              label: item.name, // Just use plain string
              description: item.description, // Store for custom rendering
            })),
          );
        } else {
          setOptions([]);
        }
      } catch (err) {
        console.error(`Error fetching suggestions for ${field}`, err);
        setOptions([]);
      }
    }, 400),
    [field, selectedBranch],
  );

  const formatValue = (val) => {
    if (type === 'date') {
      // Format as YY-MM-DD
      let numbers = val.replace(/\D/g, '').slice(0, 6);
      if (numbers.length === 0) return '';
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 4) return numbers.slice(0, 2) + '-' + numbers.slice(2);
      return numbers.slice(0, 2) + '-' + numbers.slice(2, 4) + '-' + numbers.slice(4);
    }
    if (type === 'time') {
      let numbers = val.replace(/\D/g, '').slice(0, 4);
      if (numbers.length > 2) numbers = numbers.slice(0, 2) + ':' + numbers.slice(2);
      return numbers;
    }
    return val;
  };

  const commonProps = {
    ref: (ref) => {
      if (!inputRefs.current[record.key]) inputRefs.current[record.key] = {};
      inputRefs.current[record.key][field] = ref;
    },
    size: 'small',
    value: record[field] || '',
    style: { width: '100%' },
    onFocus: () => setFocusedCell({ row: rowIdx, col: colIdx }),
    onKeyDown: (e) =>
      handleInputKeyDown(e, rowIdx, colIdx, field, rows, columnKeys, setRows, focusCell, (v) => (justCopiedRef.current = v), setIsDirty),
  };

  const autoFields = ['task', 'sub_task', 'payscale', 'group', 'level', 'pa', 'costCenter', 'workRule', 'payCode', 'wbs_io'];

  // Employee ID special logic
  if (field === 'employeeId') {
    const invalidKey = 'employeeIdInvalid';

    const handleBlur = () => {
      if (justCopiedRef.current) {
        justCopiedRef.current = false;
        return;
      }
      const currentVal = record[field]?.trim();
      const validValues = options.map((opt) => opt.value);
      // If less than 2 characters, always erase the value (invalid)
      if (currentVal && currentVal.length < 2) {
        updateCell(record.key, field, '', setRows, setIsDirty);
        return;
      }
      if (searchValue.length >= 2) {
        setRows((prevRows) =>
          prevRows.map((r) => (r.key === record.key ? { ...r, [invalidKey]: !!(currentVal && !validValues.includes(currentVal)) } : r)),
        );
      }
    };

    const handleEmployeeIdKeyDown = async (e) => {
      // Check for "9 and Tab" copy functionality first
      if (e.key === 'Tab' && e.target.value === '9') {
        e.preventDefault();
        if (rowIdx > 0) {
          const above = rows[rowIdx - 1];
          setRows((prev) =>
            prev.map((r) =>
              r.key === record.key
                ? {
                    ...r,
                    employeeId: above.employeeId,
                    name: above.name,
                    payscale: above.payscale,
                    group: above.group,
                    level: above.level,
                    pa: above.pa,
                    costCenter: above.costCenter,
                    nameLocked: !!above.name,
                    task: above.task,
                    sub_task: above.sub_task,
                    [invalidKey]: false,
                  }
                : r,
            ),
          );
          setIsDirty(true);
          justCopiedRef.current = true;
          // Move focus to In Date column after copy
          setTimeout(() => {
            const inDateColIdx = columnKeys.indexOf('inDate');
            if (inDateColIdx !== -1) {
              focusCell(rowIdx, inDateColIdx);
            }
          }, 50);
        }
        return;
      }
      
      // Handle Tab key for employeeId validation and auto-fetch
      if (e.key === 'Tab') {
        e.preventDefault(); // Prevent default tab behavior
        const currentVal = record[field]?.trim();
        const validValues = options.map((opt) => opt.value);
        
        // Check if the entered value exists in the options
        if (currentVal && currentVal.length >= 2) {
          if (validValues.includes(currentVal)) {
            // Valid employeeId - fetch details
            try {
              const res = await getEmployeeDetails(currentVal);
              const emp = res.data;
              setRows((prev) =>
                prev.map((r) =>
                  r.key === record.key
                    ? {
                        ...r,
                        name: emp.name || '',
                        payscale: emp.payscale || '',
                        group: emp.group || '',
                        level: emp.level || '',
                        pa: emp.branchPa || '',
                        costCenter: emp.costCenter || '',
                        nameLocked: true,
                        task: emp.task || '000',
                        sub_task: emp.sub_task || '000',
                        [invalidKey]: false,
                      }
                    : r,
                ),
              );
              
              // Move focus to In Date column after details are populated
              setTimeout(() => {
                const inDateColIdx = columnKeys.indexOf('inDate');
                if (inDateColIdx !== -1) {
                  focusCell(rowIdx, inDateColIdx);
                }
              }, 100);
            } catch (err) {
              console.error('Error fetching employee details', err);
            }
          } else {
            // Invalid employeeId - mark as invalid
            setRows((prev) =>
              prev.map((r) => (r.key === record.key ? { ...r, [invalidKey]: true } : r)),
            );
          }
        } else if (currentVal && currentVal.length < 2) {
          // Less than 2 characters - mark as invalid
          setRows((prev) =>
            prev.map((r) => (r.key === record.key ? { ...r, [invalidKey]: true } : r)),
          );
        }
      } else {
        // Call the original keyboard navigation handler for other keys
        handleInputKeyDown(e, rowIdx, colIdx, field, rows, columnKeys, setRows, focusCell, (v) => (justCopiedRef.current = v), setIsDirty);
      }
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {field === 'employeeId' && <StatusIndicator status={record.transactionStatus} error={record.transactionError} />}
        <AutoComplete
          classNames={{ popup: { root: `tp-table-dropdown tp-table-dropdown-${field}` } }}
          ref={(ref) => {
            if (!inputRefs.current[record.key]) inputRefs.current[record.key] = {};
            inputRefs.current[record.key][field] = ref;
          }}
          size="small"
          value={record[field] || ''}
          onFocus={() => setFocusedCell({ row: rowIdx, col: colIdx })}
          onKeyDown={handleEmployeeIdKeyDown}
          options={options}
          notFoundContent={searchValue.length >= 2 && options.length === 0 ? 'No data' : null}
          onSearch={(val) => {
            if (!selectedBranch) {
              message.error('Please select a branch before entering Employee ID');
              return;
            }
            setSearchValue(val);
            if (val.length >= 2) {
              debouncedFetch(val, record);
            } else {
              setOptions([]);
            }
          }}
          onSelect={async (val) => {
            if (!selectedBranch) {
              message.error('Please select a branch before entering Employee ID');
              return;
            }
            updateCell(record.key, field, val, setRows, setIsDirty);
            // if (field === 'employeeId') {
              try {
                const res = await getEmployeeDetails(val);
                const emp = res.data;
                // const firstName = emp.name.trim().split(' ')[0] || '';
                // const lastName = emp.name.trim().split(' ')[1] || '';
                // const formattedName = `${lastName}, ${firstName}`;
                setRows((prev) =>
                  prev.map((r) =>
                    r.key === record.key
                      ? {
                          ...r,
                          name: emp.name || '',
                          payscale: emp.payscale || '',
                          group: emp.group || '',
                          level: emp.level || '',
                          pa: emp.branchPa || '',
                          costCenter: emp.costCenter || '',
                          nameLocked: true,
                          task: emp.task || '000',
                          sub_task: emp.sub_task || '000',
                        }
                      : r,
                  ),
                );
              } catch (err) {
                console.error('Error fetching employee details', err);
              }
            // }
          }}
          onChange={(val) => {
            updateCell(record.key, field, val, setRows, setIsDirty);
            // if (field === 'employeeId') {
              setRows((prev) => prev.map((r) => (r.key === record.key ? { ...r, nameLocked: false } : r)));
            // }
          }}
          onBlur={handleBlur}
          style={{
            width: '100%',
            border: record[invalidKey] ? '2px solid #f44336' : undefined,
            borderRadius: '6px',
            boxShadow: record[invalidKey] ? '0 0 0 2px rgba(244,67,54,0.1)' : undefined,
          }}
          disabled={isRowSuccess || !selectedBranch}
        />
      </div>
    );
  }
  // Name field: lock if nameLocked is true
  if (field === 'name') {
    return (
      <Input
        {...commonProps}
        disabled={!!record.nameLocked || isRowSuccess || !selectedBranch}
        style={{ ...commonProps.style, fontWeight: record.nameLocked ? 'bold' : 'normal', color: record.nameLocked ? '#333' : undefined }}
        // placeholder="Name"
        onChange={(e) => updateCell(record.key, field, e.target.value, setRows, setIsDirty)}
      />
    );
  }

  if (autoFields.includes(field)) {
    const handleBlur = () => {
      if (justCopiedRef.current) {
        justCopiedRef.current = false;
        return;
      }
      const currentVal = record[field]?.trim();
      const validValues = options.map((opt) => opt.value);
      // If less than 2 characters, always erase the value (invalid)
      if (currentVal && currentVal.length < 2) {
        updateCell(record.key, field, '', setRows, setIsDirty);
        return;
      }
      // For sub_task: if sub_task is empty, also erase task
      if (field === 'sub_task' && !currentVal) {
        updateCell(record.key, 'task', '', setRows, setIsDirty);
      }
      // For group: if group is empty, also erase payscale
      if (field === 'group' && !currentVal) {
        updateCell(record.key, 'payscale', '', setRows, setIsDirty);
      }
      // For costCenter: if costCenter is empty, also erase pa
      if (field === 'costCenter' && !currentVal) {
        updateCell(record.key, 'pa', '', setRows, setIsDirty);
      }
      // Special logic for wbs_io: allow if first part matches any option's first part
      if (field === 'wbs_io' && currentVal) {
        const currentFirstPart = currentVal.split('.')[0];
        // Find all options whose first part matches
        const matchingOptions = validValues.filter(
          (val) => val.split('.')[0] === currentFirstPart
        );
        if (matchingOptions.length === 1) {
          // If exactly one match, set the full value
          updateCell(record.key, field, matchingOptions[0], setRows, setIsDirty);
          return;
        }
        if (matchingOptions.length > 1) {
          // If multiple matches, allow the user's input (do not erase)
          return;
        }
        // If not matching, erase
        updateCell(record.key, field, '', setRows, setIsDirty);
        return;
      }
      if (field === 'payCode' && currentVal) {
        const currentFirstPart = currentVal.split('-')[0];
        // Find all options whose first part matches
        const matchingOptions = validValues.filter(
          (val) => val.split('-')[0] === currentFirstPart
        );
        if (matchingOptions.length === 1) {
          // If exactly one match, set the full value
          updateCell(record.key, field, matchingOptions[0], setRows, setIsDirty);
          // --- Add this: update unit and amount as in onSelect ---
          const matchObj = options.find(opt => opt.value === matchingOptions[0]);
          const unit = matchObj?.unit;
          setRows((prev) =>
            prev.map((r) =>
              r.key === record.key
                ? {
                    ...r,
                    unit,
                    amount:
                      unit === 'MONEY'
                        ? r.amount?.toString().startsWith('$')
                          ? r.amount
                          : `$${r.amount || ''}`
                        : r.amount?.toString().replace(/^\$/, ''),
                  }
                : r,
            ),
          );
          return;
        }
        if (matchingOptions.length > 1) {
          // If multiple matches, allow the user's input (do not erase)
          return;
        }
        // If not matching, erase
        updateCell(record.key, field, '', setRows, setIsDirty);
        return;
      }

      // --- Task field logic ---
      if (field === 'task' && currentVal) {
        // If the value is valid (in dropdown) and sub_task is empty, set sub_task to '000'
        if (validValues.includes(currentVal) && !record.sub_task) {
          setRows((prevRows) =>
            prevRows.map((r) =>
              r.key === record.key
                ? { ...r, sub_task: '000' }
                : r
            )
          );
        }
      }

      // If options were fetched (user typed >=2 chars), validate typed value against options.
      // If no match, erase the typed value.
      if (searchValue.length >= 2) {
        if (validValues.length === 0 || (currentVal && !validValues.includes(currentVal))) {
          updateCell(record.key, field, '', setRows, setIsDirty);
          return;
        }
      }
      // Otherwise (no options fetched) allow free text to remain.

      // Only validate if options were fetched (i.e., user typed >=2 chars)
      // if (searchValue.length >= 2) {
      //   if (['payscale', 'group', 'level', 'task', 'sub_task', 'pa', 'costCenter'].includes(field)) {
      //     // If the value is not in the dropdown, always erase it (even if nameLocked is true)
      //     if (currentVal && !validValues.includes(currentVal)) {
      //       updateCell(record.key, field, '', setRows, setIsDirty);
      //     }
      //     return;
      //   }

      //   // For all other autocomplete fields (except employeeId and name), only allow if in dropdown
      //   if (autoFields.includes(field) && field !== 'employeeId' && field !== 'name' && currentVal && !validValues.includes(currentVal)) {
      //     updateCell(record.key, field, '', setRows, setIsDirty);
      //   }
      // }
    };

    return (
      <AutoComplete
        classNames={{ popup: { root: `tp-table-dropdown tp-table-dropdown-${field}` } }}
        {...commonProps}
        options={options}
        notFoundContent={searchValue.length >= 2 && options.length === 0 ? 'No data' : null}
        optionRender={(option) => (
          <Tooltip
            title={
              option.data.description ? (
                <div>
                  <div>
                    <strong>{option.data.value}</strong>
                  </div>
                  <div>{option.data.description}</div>
                </div>
              ) : (
                option.data.value
              )
            }
            placement="right"
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div>{option.data.value}</div>
              {option.data.description && <div style={{ fontSize: '11px', color: '#888' }}>{option.data.description}</div>}
            </div>
          </Tooltip>
        )}
        onSearch={(val) => {
          if (!selectedBranch) {
            message.error('Please select a branch before entering data');
            return;
          }
          setSearchValue(val);
          if (val.length >= 2) {
            debouncedFetch(val, record);
          } else {
            setOptions([]);
          }
        }}
        onSelect={(val, option) => {
          if (!selectedBranch) {
            message.error('Please select a branch before entering data');
            return;
          }
          // Ensure we're only setting the value (name), not the label text
          const actualValue = option?.value || val;
          updateCell(record.key, field, actualValue, setRows, setIsDirty);
          if (field === 'payCode') {
            const unit = option?.unit;
            console.log('Selected payCode unit:', unit);
            setRows((prev) =>
              prev.map((r) =>
                r.key === record.key
                  ? {
                      ...r,
                      unit, // Store the unit
                      amount:
                        unit === 'MONEY'
                          ? r.amount?.toString().startsWith('$')
                            ? r.amount
                            : `$${r.amount || ''}`
                          : r.amount?.toString().replace(/^\$/, ''),
                    }
                  : r,
              ),
            );
          }
          if (field === 'task' && !record.sub_task) {
            setRows((prevRows) =>
              prevRows.map((r) =>
                r.key === record.key
                  ? { ...r, sub_task: '000' }
                  : r
              )
            );
          }
        }}
        onChange={(val) => updateCell(record.key, field, val, setRows, setIsDirty)}
        onBlur={handleBlur}
        disabled={isRowSuccess || !selectedBranch}
      />
    );
  }

  if (field === 'inDate' || field === 'outDate') {
    const isWarning = isFutureDate(record[field]);
    const isInvalid = record[field] && !isValidShortDate(record[field]);
    return (
      <Input
        {...commonProps}
        className="input-small-placeholder"
        placeholder="MM-DD-YY"
        onChange={(e) => updateCell(record.key, field, formatValue(e.target.value), setRows, setIsDirty)}
        style={{
          ...commonProps.style,
          border: isInvalid ? '2px solid #f44336' : isWarning ? '2px solid #FFD600' : undefined,
          background: isWarning ? '#FFFDE7' : undefined, // optional: light yellow background
        }}
        disabled={isRowSuccess || !selectedBranch}
      />
    );
  }

  if (field === 'inTime' || field === 'outTime') {
    const isInvalid = record[field] && !isValidTime(record[field]);
    return (
      <Input
        {...commonProps}
        placeholder="HH:MM"
        className="input-small-placeholder"
        onChange={(e) => updateCell(record.key, field, formatValue(e.target.value), setRows, setIsDirty)}
        style={{
          ...commonProps.style,
          border: isInvalid ? '2px solid #f44336' : undefined,
        }}
        disabled={isRowSuccess || !selectedBranch}
      />
    );
  }

  return (
    <Input
      {...commonProps}
      // placeholder={type === 'date' ? 'YYYY-MM-DD' : type === 'time' ? 'HH:mm' : ''}
      onChange={(e) => updateCell(record.key, field, formatValue(e.target.value), setRows, setIsDirty)}
      disabled={isRowSuccess || !selectedBranch}
    />
  );
};

/** Factory for AntD render */
export const renderCell =
  ({ field, type, selectedBranch, ...rest }) =>
  (text, record, rowIdx) => {
    const colIdx = rest.columnKeys.indexOf(field);
    return <CellRenderer {...rest} record={record} rowIdx={rowIdx} colIdx={colIdx} field={field} type={type} selectedBranch={selectedBranch} />;
  };

export const timeInputWidths = {
  employeeId: '8%',
  name: '9%',
  inDate: '6%',
  outDate: '6%',
  inTime: '5%',
  outTime: '5%',
  wbs_io: '13%',
  task: '5%',
  sub_task: '5%',
  payscale: '6%',
  group: '5%',
  level: '10%',
  workRule: '11%',
  pa: '6%',
  costCenter: '8%',
};

export const payInputWidths = {
  employeeId: '8%',
  name: '10%',
  inDate: '6%',
  payCode: '14%',
  amount: '6%',
  wbs_io: '13%',
  task: '6%',
  sub_task: '6%',
  payscale: '7%',
  group: '5%',
  level: '14%',
};

const withAsterisk = (title, field, tab) => {
  const isRequired = (tab === 'time' && requiredFieldsTime.includes(field)) || (tab === 'pay' && requiredFieldsPay.includes(field));
  return (
    <span>
      {title}
      {isRequired && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
    </span>
  );
};

export const getTimeColumns = (cellRendererFactory, tab = 'time') => [
  {
    title: withAsterisk('Employee ID', 'employeeId', tab),
    exportTitle: 'Employee ID',
    dataIndex: 'employeeId',
    key: 'employeeId',
    width: timeInputWidths.employeeId,
    fixed: 'left',
    render: cellRendererFactory('employeeId', 'text'),
  },
  {
    title: withAsterisk('Name', 'name', tab),
    exportTitle: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: timeInputWidths.name,
    fixed: 'left',
    render: cellRendererFactory('name', 'text'),
  },
  {
    title: withAsterisk('In Date', 'inDate', tab),
    exportTitle: 'In Date',
    dataIndex: 'inDate',
    key: 'inDate',
    width: timeInputWidths.inDate,
    render: cellRendererFactory('inDate', 'date'),
  },
  {
    title: 'Out Date',
    dataIndex: 'outDate',
    key: 'outDate',
    width: timeInputWidths.outDate,
    render: cellRendererFactory('outDate', 'date'),
  },
  {
    title: withAsterisk('In', 'inTime', tab),
    exportTitle: 'In',
    dataIndex: 'inTime',
    key: 'inTime',
    width: timeInputWidths.inTime,
    render: cellRendererFactory('inTime', 'time'),
  },
  {
    title: 'Out',
    dataIndex: 'outTime',
    key: 'outTime',
    width: timeInputWidths.outTime,
    render: cellRendererFactory('outTime', 'time'),
  },
  {
    title: 'WBS/IO',
    dataIndex: 'wbs_io',
    key: 'wbs_io',
    width: timeInputWidths.wbs_io,
    render: cellRendererFactory('wbs_io', 'text'),
  },
  { title: 'Task', dataIndex: 'task', key: 'task', width: timeInputWidths.task, render: cellRendererFactory('task', 'text') },
  { title: 'Sub', dataIndex: 'sub_task', key: 'sub_task', width: timeInputWidths.sub_task, render: cellRendererFactory('sub_task', 'text') },
  { title: 'Payscale', dataIndex: 'payscale', key: 'payscale', width: timeInputWidths.payscale, render: cellRendererFactory('payscale', 'text') },
  { title: 'Group', dataIndex: 'group', key: 'group', width: timeInputWidths.group, render: cellRendererFactory('group', 'text') },
  { title: 'Lvl', dataIndex: 'level', key: 'level', width: timeInputWidths.level, render: cellRendererFactory('level', 'text') },
  { title: 'Work Rule', dataIndex: 'workRule', key: 'workRule', width: timeInputWidths.workRule, render: cellRendererFactory('workRule', 'text') },
  { title: 'PA', dataIndex: 'pa', key: 'pa', width: timeInputWidths.pa, render: cellRendererFactory('pa', 'text') },
  {
    title: 'Cost Center',
    dataIndex: 'costCenter',
    key: 'costCenter',
    width: timeInputWidths.costCenter,
    render: cellRendererFactory('costCenter', 'text'),
  },
];

export const getPayColumns = (cellRendererFactory, tab = 'pay') => [
  {
    title: withAsterisk('Employee ID', 'employeeId', tab),
    exportTitle: 'Employee ID',
    dataIndex: 'employeeId',
    key: 'employeeId',
    width: payInputWidths.employeeId,
    fixed: 'left',
    render: cellRendererFactory('employeeId', 'text'),
  },
  {
    title: withAsterisk('Name', 'name', tab),
    exportTitle: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: payInputWidths.name,
    fixed: 'left',
    render: cellRendererFactory('name', 'text'),
  },
  {
    title: withAsterisk('In Date', 'inDate', tab),
    exportTitle: 'In Date',
    dataIndex: 'inDate',
    key: 'inDate',
    width: payInputWidths.inDate,
    render: cellRendererFactory('inDate', 'date'),
  },
  { title: withAsterisk('Pay Code', 'payCode', tab), exportTitle: 'Pay Code', dataIndex: 'payCode', key: 'payCode', width: payInputWidths.payCode, render: cellRendererFactory('payCode', 'text') },
  { title: withAsterisk('Amount', 'amount', tab), exportTitle: 'Amount', dataIndex: 'amount', key: 'amount', width: payInputWidths.amount, render: cellRendererFactory('amount', 'text') },
  {
    title: 'WBS/IO',
    dataIndex: 'wbs_io',
    key: 'wbs_io',
    width: payInputWidths.wbs_io,
    render: cellRendererFactory('wbs_io', 'text'),
  },
  { title: 'Task', dataIndex: 'task', key: 'task', width: payInputWidths.task, render: cellRendererFactory('task', 'text') },
  { title: 'Sub', dataIndex: 'sub_task', key: 'sub_task', width: payInputWidths.sub_task, render: cellRendererFactory('sub_task', 'text') },
  { title: 'Payscale', dataIndex: 'payscale', key: 'payscale', width: payInputWidths.payscale, render: cellRendererFactory('payscale', 'text') },
  { title: 'Group', dataIndex: 'group', key: 'group', width: payInputWidths.group, render: cellRendererFactory('group', 'text') },
  { title: 'Lvl', dataIndex: 'level', key: 'level', width: payInputWidths.level, render: cellRendererFactory('level', 'text') },
];

export const formatDateToYYYY = (val) => {
  // Expects MM-DD-YY, returns MM-DD-YYYY
  if (!val) return val;
  const parts = val.split('-');
  if (parts.length !== 3) return val;
  let [mm, dd, yy] = parts;
  if (yy.length === 2) {
    // Assume 20xx for 00-49, 19xx for 50-99 (or adjust as needed)
    const yyyy = parseInt(yy, 10) < 50 ? '20' + yy : '19' + yy;
    return `${mm}-${dd}-${yyyy}`;
  }
  return val;
};

export const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  // Accepts MM-DD-YY or MM-DD-YYYY
  const parts = dateStr.split('-');
  if (parts.length !== 3) return false;
  let [mm, dd, yy] = parts;
  if (yy.length === 2) {
    yy = parseInt(yy, 10) < 50 ? '20' + yy : '19' + yy;
  }
  // Use ISO format for Date: YYYY-MM-DD
  const inputDate = new Date(`${yy}-${mm}-${dd}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Compare only the date part
  return inputDate.getTime() > today.getTime();
};

export const isValidShortDate = (val) => {
  // Expects MM-DD-YY
  if (!val || val.length !== 8) return false;
  const parts = val.split('-');
  if (parts.length !== 3) return false;
  const [mm, dd, yy] = parts;
  if (mm.length !== 2 || dd.length !== 2 || yy.length !== 2 || isNaN(mm) || isNaN(dd) || isNaN(yy)) return false;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
};

export const isValidTime = (val) => {
  // Expects HH:MM
  if (!val || val.length !== 5) return false;
  const parts = val.split(':');
  if (parts.length !== 2) return false;
  const [hh, mm] = parts;
  if (hh.length !== 2 || mm.length !== 2 || isNaN(hh) || isNaN(mm)) return false;
  const hours = parseInt(hh, 10);
  const minutes = parseInt(mm, 10);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};
